import { DateTime } from 'luxon'

import { UpdateTemplateServiceVendorParams } from '../../../../../hooks/useEzSchedule/types'
import { TemplateService } from '../../../../../models'

export interface TimelineBounds {
    scheduleStart: DateTime | null
    scheduleEnd: DateTime | null
}

export interface DateFieldErrors {
    workStart?: string
    workEnd?: string
    transferStart?: string
    transferEnd?: string
    destUnitReady?: string
    dtUnitReady?: string
}

export const inclusiveDayCount = (start: DateTime, end: DateTime) =>
    Math.round(end.startOf('day').diff(start.startOf('day'), 'days').days) + 1

export const formatTimelineChip = (start: DateTime, end: DateTime) => {
    const days = inclusiveDayCount(start, end)
    const sameYear = start.year === end.year
    const startLabel = start.toFormat(sameYear ? 'LLL d' : 'LLL d, yyyy')
    const endLabel = end.toFormat(sameYear ? 'LLL d' : 'LLL d, yyyy')
    const dayLabel = days === 1 ? 'day' : 'days'
    return `${startLabel} – ${endLabel} · ${days} ${dayLabel}`
}

export const dateFieldErrors = (input: {
    workStart: DateTime | null
    workEnd: DateTime | null
    transferStart?: DateTime | null
    transferEnd?: DateTime | null
    destUnitReady?: DateTime | null
    dtUnitReady?: DateTime | null
    bounds: TimelineBounds
}): DateFieldErrors => {
    const errors: DateFieldErrors = {}
    const {
        workStart,
        workEnd,
        transferStart,
        transferEnd,
        destUnitReady,
        dtUnitReady,
        bounds,
    } = input

    if (workStart && bounds.scheduleStart && workStart < bounds.scheduleStart) {
        errors.workStart = 'Must be on or after schedule start date'
    }
    if (workStart && bounds.scheduleEnd && workStart >= bounds.scheduleEnd) {
        errors.workStart = 'Must be before schedule end date'
    }
    if (workEnd && workStart && workEnd <= workStart) {
        errors.workEnd = 'Must be after work start date'
    }
    if (workEnd && bounds.scheduleEnd && workEnd > bounds.scheduleEnd) {
        errors.workEnd = 'Must be on or before schedule end date'
    }
    if (transferStart && workStart && transferStart < workStart) {
        errors.transferStart = 'Must be on or after work start date'
    }
    if (transferStart && workEnd && transferStart >= workEnd) {
        errors.transferStart = 'Must be before work end date'
    }
    if (transferEnd && transferStart && transferEnd <= transferStart) {
        errors.transferEnd = 'Must be after transfer start date'
    }
    if (transferEnd && workEnd && transferEnd >= workEnd) {
        errors.transferEnd = 'Must be before work end date'
    }
    if (
        destUnitReady &&
        workStart &&
        destUnitReady.startOf('day') < workStart.startOf('day')
    ) {
        errors.destUnitReady = 'Must be on or after work start date'
    }
    if (
        destUnitReady &&
        workEnd &&
        destUnitReady.startOf('day') > workEnd.startOf('day')
    ) {
        errors.destUnitReady = 'Must be on or before work end date'
    }
    if (
        dtUnitReady &&
        workStart &&
        dtUnitReady.startOf('day') < workStart.startOf('day')
    ) {
        errors.dtUnitReady = 'Must be on or after work start date'
    }
    if (
        destUnitReady &&
        dtUnitReady &&
        dtUnitReady.startOf('day') <= destUnitReady.startOf('day')
    ) {
        errors.dtUnitReady = 'Must be after the destination unit ready date'
    }
    if (
        dtUnitReady &&
        workEnd &&
        dtUnitReady.startOf('day') > workEnd.startOf('day')
    ) {
        errors.dtUnitReady = 'Must be on or before work end date'
    }

    return errors
}

export const workRangeMessage = (
    start: DateTime | null,
    end: DateTime | null,
    bounds: TimelineBounds,
) => {
    if (!start || !end) return null
    const errors = dateFieldErrors({
        workStart: start,
        workEnd: end,
        bounds,
    })
    return errors.workStart ?? errors.workEnd ?? null
}

const sameDay = (left: DateTime, right: DateTime) =>
    left.startOf('day').toMillis() === right.startOf('day').toMillis()

export const transferStartMessage = (
    day: DateTime,
    workStart: DateTime,
    workEnd: DateTime,
) => {
    if (day.startOf('day') < workStart.startOf('day')) {
        return 'Must be on or after the work start'
    }
    if (day.startOf('day') >= workEnd.startOf('day')) {
        return 'Must be before the work end'
    }
    return null
}

export const transferEndMessage = (
    day: DateTime,
    transferStart: DateTime | null,
    workEnd: DateTime,
) => {
    if (!transferStart) return 'Set a transfer start first'
    if (
        sameDay(day, transferStart) ||
        day.startOf('day') <= transferStart.startOf('day')
    ) {
        return 'Must be after the transfer start'
    }
    if (day.startOf('day') >= workEnd.startOf('day')) {
        return 'Must be before the work end'
    }
    return null
}

export const readyDateMessage = (
    day: DateTime,
    workStart: DateTime,
    workEnd: DateTime,
    minDay: DateTime | null,
    afterLabel?: string,
) => {
    if (day.startOf('day') < workStart.startOf('day')) {
        return 'Must be on or after the work start'
    }
    if (day.startOf('day') > workEnd.startOf('day')) {
        return 'Must be on or before the work end'
    }
    if (minDay && day.startOf('day') < minDay.startOf('day')) {
        const when = minDay.toFormat('MM/dd/yyyy')
        return afterLabel
            ? `Must be after ${afterLabel} (${when})`
            : `Must be on or after ${when}`
    }
    return null
}

export const enumerateDays = (start: DateTime, end: DateTime) => {
    const days: DateTime[] = []
    let cursor = start.startOf('day')
    const last = end.startOf('day')
    while (cursor <= last) {
        days.push(cursor)
        cursor = cursor.plus({ days: 1 })
    }
    return days
}

export const toUtcIso = (date: DateTime, boundary: 'start' | 'end') => {
    const zoned = boundary === 'start' ? date.startOf('day') : date.endOf('day')
    return zoned.toUTC().toISO()
}

const parseZoned = (value: string | null | undefined, zone: string) => {
    if (!value) return null
    const parsed = DateTime.fromISO(value).setZone(zone)
    return parsed.isValid ? parsed : null
}

export const clampedVendorUpdates = (
    services: TemplateService[],
    workStart: DateTime,
    workEnd: DateTime,
    zone: string,
): UpdateTemplateServiceVendorParams[] => {
    const windowStart = workStart.startOf('day')
    const windowEnd = workEnd.endOf('day')
    const updates: UpdateTemplateServiceVendorParams[] = []

    services.forEach((service) => {
        service.template_service_vendors.forEach((vendor) => {
            if (vendor.id < 0) return
            const start = parseZoned(vendor.start_date, zone)
            const end = parseZoned(vendor.end_date, zone)
            if (!start && !end) return

            let nextStart = start
            let nextEnd = end
            if (nextStart && nextStart < windowStart) nextStart = windowStart
            if (nextEnd && nextEnd > windowEnd) nextEnd = windowEnd
            if (nextStart && nextEnd && nextStart > nextEnd) {
                nextStart = windowStart
                nextEnd = windowEnd
            }

            const startChanged =
                (start?.toMillis() ?? null) !== (nextStart?.toMillis() ?? null)
            const endChanged =
                (end?.toMillis() ?? null) !== (nextEnd?.toMillis() ?? null)
            if (!startChanged && !endChanged) return

            const params: UpdateTemplateServiceVendorParams = {
                templateServiceVendorId: vendor.id,
            }
            if (nextStart) {
                const iso = toUtcIso(nextStart, 'start')
                if (iso) params.start_date = iso
            }
            if (nextEnd) {
                const iso = toUtcIso(nextEnd, 'end')
                if (iso) params.end_date = iso
            }
            updates.push(params)
        })
    })

    return updates
}
